<template>
  <UiContainer class="register-page">
    <div class="register-page__card-wrap">
      <UiCard class="register-page__card">
        <h1 class="register-page__title">Регистрация</h1>
        <form class="register-page__form" @submit.prevent="register">
          <div class="register-page__row">
            <div class="register-page__field">
              <label class="register-page__label" for="reg-firstName">Имя</label>
              <UiInput id="reg-firstName" v-model="firstName" placeholder="Имя" required />
            </div>
            <div class="register-page__field">
              <label class="register-page__label" for="reg-lastName">Фамилия</label>
              <UiInput id="reg-lastName" v-model="lastName" placeholder="Фамилия" required />
            </div>
          </div>

          <div class="register-page__field">
            <label class="register-page__label" for="reg-email">Email</label>
            <UiInput
              id="reg-email"
              v-model="email"
              type="email"
              autocomplete="email"
              placeholder="email@example.com"
              required
            />
          </div>

          <div class="register-page__field">
            <label class="register-page__label" for="reg-password">Пароль</label>
            <UiInput
              id="reg-password"
              v-model="password"
              type="password"
              placeholder="••••••••"
              autocomplete="new-password"
              minlength="8"
              required
            />
            <span class="register-page__hint">Не менее 8 символов</span>
          </div>

          <div class="register-page__divider" />

          <h2 class="register-page__subtitle">Данные компании</h2>

          <div class="register-page__field">
            <label class="register-page__label" for="reg-inn">ИНН компании</label>
            <UiInput
              id="reg-inn"
              v-model="companyInn"
              placeholder="10 или 12 цифр"
              maxlength="12"
              input-filter="digits"
              required
            />
            <span class="register-page__hint">10 цифр для юридических лиц, 12 для ИП</span>
          </div>

          <div class="register-page__field">
            <label class="register-page__label" for="reg-companyName">Название компании</label>
            <UiInput id="reg-companyName" v-model="companyName" placeholder="Название" required />
          </div>

          <label class="register-page__consent">
            <input v-model="agreeTerms" type="checkbox" />
            <span>
              Я соглашаюсь с
              <a href="/terms" target="_blank" rel="noopener">условиями использования</a> и
              <a href="/privacy" target="_blank" rel="noopener">политикой конфиденциальности</a>
            </span>
          </label>
          <label class="register-page__consent">
            <input v-model="agreeProcessing" type="checkbox" />
            <span>Я соглашаюсь на обработку персональных данных</span>
          </label>

          <UiButton type="submit" block :loading="loading"> Зарегистрироваться </UiButton>
        </form>
        <p class="register-page__footer">
          Уже есть аккаунт?
          <router-link to="/login" class="register-page__link">Войти</router-link>
        </p>
      </UiCard>
    </div>
  </UiContainer>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthSession } from '@/modules/auth/public'
import { useAppNotifications } from '@/modules/common/public'
import UiButton from '@/shared/ui/UiButton.vue'
import UiCard from '@/shared/ui/UiCard.vue'
import UiContainer from '@/shared/ui/UiContainer.vue'
import UiInput from '@/shared/ui/UiInput.vue'

const router = useRouter()
const authStore = useAuthSession()
const notificationStore = useAppNotifications()

const firstName = ref('')
const lastName = ref('')
const email = ref('')
const password = ref('')
const companyInn = ref('')
const companyName = ref('')
const agreeTerms = ref(false)
const agreeProcessing = ref(false)
const loading = ref(false)

async function register() {
  const errors: string[] = []
  if (!firstName.value.trim()) errors.push('Имя')
  if (!lastName.value.trim()) errors.push('Фамилия')
  if (!email.value.trim()) errors.push('Email')
  if (!password.value.trim()) errors.push('Пароль')
  if (!companyInn.value.trim()) errors.push('ИНН')
  if (!companyName.value.trim()) errors.push('Название компании')

  if (errors.length > 0) {
    notificationStore.showError(`Заполните обязательные поля: ${errors.join(', ')}`)
    return
  }

  const innPattern = /^\d{10}$|^\d{12}$/
  if (!agreeTerms.value || !agreeProcessing.value) {
    notificationStore.showError(
      'Подтвердите согласие с условиями использования и на обработку персональных данных',
    )
    return
  }

  if (password.value.length < 8) {
    notificationStore.showError('Пароль должен содержать не менее 8 символов')
    return
  }

  if (!innPattern.test(companyInn.value.trim())) {
    notificationStore.showError('ИНН должен содержать 10 или 12 цифр')
    return
  }

  loading.value = true
  try {
    const registerData = {
      firstName: firstName.value.trim(),
      lastName: lastName.value.trim(),
      email: email.value.trim(),
      password: password.value,
      inn: companyInn.value.trim(),
      companyName: companyName.value.trim(),
    }

    const result = await authStore.register(registerData)

    if (result.success) {
      notificationStore.showSuccess(
        result.message || 'Регистрация завершена. Подтвердите email по ссылке из письма',
      )
      router.push({
        path: '/login',
        query: {
          emailConfirmationSent: '1',
          email: registerData.email,
        },
      })
    } else {
      notificationStore.showError(result.error || 'Ошибка регистрации')
    }
  } catch {
    notificationStore.showError('Произошла ошибка при регистрации')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.register-page {
  padding-top: 32px;
  padding-bottom: 32px;
}

.register-page__card-wrap {
  max-width: 560px;
  margin: 0 auto;
}

.register-page__card {
  padding: 24px;
}

.register-page__title {
  margin: 0 0 24px;
  font-size: 24px;
  font-weight: 600;
  text-align: center;
}

.register-page__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.register-page__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 600px) {
  .register-page__row {
    grid-template-columns: minmax(0, 1fr);
  }
}

.register-page__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.register-page__label {
  font-size: 14px;
  font-weight: 500;
  color: #334155;
}

.register-page__hint {
  font-size: 12px;
  color: #586578;
}

.register-page__consent {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #475569;
}

.register-page__consent input {
  margin-top: 2px;
  flex-shrink: 0;
}

.register-page__consent a {
  color: #0f172a;
}

.register-page__divider {
  height: 1px;
  background: #e2e8f0;
  margin: 8px 0;
}

.register-page__subtitle {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
}

.register-page__footer {
  margin: 24px 0 0;
  text-align: center;
  color: #586578;
  font-size: 14px;
}

.register-page__link {
  color: #0f172a;
  font-weight: 500;
  text-decoration: none;
}

.register-page__link:hover {
  text-decoration: underline;
}
</style>
